import { Component, computed, effect, inject, input, signal, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { requiredWithMessageValidator } from "@kno2/common/validators";
import { AdminLicenseTypesService, LicenseType } from "@kno2/data-access/license-types";
import { SubscriptionPlansService } from "@kno2/data-access/subscription-plans";
import { DialogService } from "@kno2/shared/dialog";
import { FormInputComponent, FormRadioInputComponent, FormSelectComponent, FormService } from "@kno2/shared/forms";
import { FormInputErrorComponent } from "@kno2/shared/forms/form-input-error/form-input-error.component";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { catchError, from, map, of, switchMap, timer } from "rxjs";

interface AdminLicenseTypeForm {
    name: FormControl<string>;
    description: FormControl<string>;
    category: FormControl<"Device" | "App" | "LicensedPlan" | "LicensedConnector" | null>;
    subscriptionId: FormControl<number | null>;
    subscriptionDurationDays: FormControl<number | null>;
}

@Component({
    selector: "kno2-admin-license-types-edit-modal",
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NotificationAlertComponent,
        FormInputComponent,
        FormInputErrorComponent,
        FormRadioInputComponent,
        FormSelectComponent
    ],
    templateUrl: "./admin-license-types-edit-modal.component.html"
})
export class AdminLicenseTypesEditModalComponent {
    private readonly dialogService = inject(DialogService);
    private readonly adminLicenseTypesService = inject(AdminLicenseTypesService);
    private readonly subscriptionPlansService = inject(SubscriptionPlansService);
    private readonly formService = inject(FormService);
    private readonly fb = inject(FormBuilder);

    protected nameInput = viewChild<FormInputComponent>("nameInput");

    protected form = this.fb.group<AdminLicenseTypeForm>({
        name: this.fb.control<string>("", {
            validators: [requiredWithMessageValidator("Name is required.")],
            asyncValidators: [this.licenseTypeNameExistsValidator()],
            updateOn: "change"
        }),
        description: this.fb.control<string>("", [requiredWithMessageValidator("Description is required.")]),
        category: this.fb.control<"Device" | "App" | "LicensedPlan" | "LicensedConnector" | null>(null, [
            requiredWithMessageValidator("Category is required.")
        ]),
        subscriptionId: this.fb.control<number | null>(null),
        subscriptionDurationDays: this.fb.control<number | null>(null)
    });

    protected namePattern = { A: { pattern: new RegExp("^[\\w\\d\\-_]*$") } };

    protected error = signal<string>("");
    protected errorMessages = signal<string[]>([]);
    protected isSubmitting = signal<boolean>(false);

    public adminLicenseType = input<LicenseType>({} as LicenseType);

    private allSubscriptionPlans = this.subscriptionPlansService.getAllSubscriptionPlans();
    protected subscriptionPlanOptions = computed(() => this.allSubscriptionPlans().map(({ id, productId }) => [productId, id]));

    protected categoryOptions = [
        ["Device", "Device"],
        ["App", "App"],
        ["Licensed Plan", "LicensedPlan"],
        ["Licensed Connector", "LicensedConnector"]
    ];

    protected categoryValue = toSignal(this.form.controls.category.valueChanges, { initialValue: null });
    protected subscriptionValue = toSignal(this.form.controls.subscriptionId.valueChanges, { initialValue: null });
    protected nameControlStatus = toSignal(this.form.controls.name.statusChanges, { initialValue: "VALID" });
    protected isAsyncValidatorRunning = computed(() => this.nameControlStatus() === "PENDING");

    protected isLicensedPlan = computed(() => this.categoryValue() === "LicensedPlan");

    public setFormValuesEffect = effect(() => {
        const adminLicenseTypeValue = this.adminLicenseType();
        if (adminLicenseTypeValue && Object.keys(adminLicenseTypeValue).length > 0) {
            this.setFormValues();
            this.setFormValuesEffect.destroy();
        }
    });

    public categoryChangeEffect = effect(() => {
        const category = this.categoryValue();
        const subscriptionControl = this.form.controls.subscriptionId;

        if (category === "LicensedPlan") {
            subscriptionControl.setValidators([requiredWithMessageValidator("Subscription is required.")]);
        } else {
            subscriptionControl.clearValidators();
        }

        subscriptionControl.updateValueAndValidity();
    });

    public subscriptionChangeEffect = effect(() => {
        const subscription = this.subscriptionValue();
        const durationControl = this.form.controls.subscriptionDurationDays;

        if (subscription && this.isLicensedPlan()) {
            durationControl.setValidators([requiredWithMessageValidator("Subscription Duration is required.")]);
        } else {
            durationControl.clearValidators();
            if (!subscription) {
                durationControl.setValue(null);
            }
        }
        durationControl.updateValueAndValidity();
    });

    public cancel(): void {
        this.dialogService.close();
    }

    public async save(): Promise<void> {
        this.form.markAllAsTouched();
        const formErrors = this.formService.getErrorValues<string>(this.form);
        if (formErrors.length) {
            return;
        }

        this.isSubmitting.set(true);

        try {
            const formValue = this.form.getRawValue();
            const licenseTypeData: LicenseType = {
                id: this.adminLicenseType()?.id,
                name: formValue.name,
                description: formValue.description,
                category: formValue.category,
                subscriptionId: formValue.subscriptionId,
                subscriptionDurationDays: formValue.subscriptionDurationDays
            };

            if (this.adminLicenseType()?.id) {
                await this.adminLicenseTypesService.updateAdminLicenseType(licenseTypeData);
            } else {
                await this.adminLicenseTypesService.createAdminLicenseType(licenseTypeData);
            }

            this.dialogService.close(licenseTypeData);
        } catch (err) {
            this.error.set("Error saving license type.");
            if (err?.error?.modelState) {
                this.errorMessages.set(Object.values(err.error.modelState));
            }
        } finally {
            this.isSubmitting.set(false);
        }
    }

    public setFormValues(): void {
        const licenseType = this.adminLicenseType();
        this.form.controls.name.clearAsyncValidators();

        this.form.patchValue({
            name: licenseType.name || "",
            description: licenseType.description || "",
            category: licenseType.category || null,
            subscriptionId: licenseType.subscriptionId || null,
            subscriptionDurationDays: licenseType.subscriptionDurationDays || null
        });

        Object.keys(this.form.controls).forEach((key) => {
            this.form.get(key)?.updateValueAndValidity();
        });

        this.form.controls.name.setAsyncValidators([this.licenseTypeNameExistsValidator()]);
    }

    protected focusNameInput = effect(() => {
        const input = this.nameInput();
        if (input) {
            input.focus();
        }
    });

    private licenseTypeNameExistsValidator(): AsyncValidatorFn {
        return (control: AbstractControl) => {
            if (!control.value?.trim()) {
                return of(null);
            }

            const excludeId = this.adminLicenseType()?.id;
            return timer(500).pipe(
                switchMap(() => from(this.adminLicenseTypesService.checkNameExists(control.value, excludeId))),
                map((response) => (response ? { exists: "Name already exists." } : null)),
                catchError(() => of(null))
            );
        };
    }
}
